// client/src/components/configuracoes/disponibilidade/ResultadoDisponibilidade.js
import React, { useMemo, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { Clock, Info, XCircle, Lock, Coffee, CalendarOff, AlertTriangle, Combine, GitPullRequestDraft, TimerOff, Share2, User, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AgendaShareable from './AgendaShareable';
import { useMe } from '../../../hooks/useAuth';

const TimeSlot = ({ slot }) => {
    if (!slot || typeof slot !== 'object') return null;
    
    const baseClasses = "time-slot-item";
    let statusClasses = "";
    let icon = <Clock size={14} className="flex-shrink-0" />;
    let tooltip = `Horário ${slot.horario}`;
    
    // ✅ Verifica o status do slot e aplica a classe SCSS correta
    switch (slot.status) {
        case 'Livre':
            statusClasses = "is-livre";
            tooltip += " - Disponível";
            break;
            
        case 'Ocupado':
            // ✅ CORRETO: Diferencia entre profissional e recurso
            if (slot.tipoOcupacao === 'Profissional' || slot.motivo?.includes('Profissional')) {
                statusClasses = "is-ocupado-profissional";
                tooltip = `${slot.horario} - Ocupado por profissional${slot.motivo ? `: ${slot.motivo}` : ''}`;
                icon = <User size={14} className="flex-shrink-0" />;
            } else {
                statusClasses = "is-ocupado-recurso";
                tooltip = `${slot.horario} - Ocupado por recurso${slot.motivo ? `: ${slot.motivo}` : ''}`;
                icon = <Wrench size={14} className="flex-shrink-0" />;
            }
            break;
            
        case 'Insuficiente':
            statusClasses = "is-insuficiente";
            tooltip += " - Duração insuficiente (não é possível concluir o serviço)";
            icon = <AlertTriangle size={14} className="flex-shrink-0" />;
            break;
            
        case 'Intervalo':
            statusClasses = "is-intervalo";
            tooltip += " - Horário de intervalo/pausa";
            icon = <Coffee size={14} className="flex-shrink-0" />;
            break;
            
        case 'Bloqueado':
        case 'BloqueioManual':
        case 'BloqueioAntecedencia':
        case 'HorarioPassado':
        case 'DataPassada':
        case 'ForaExpedienteUnidade':
        case 'ForaExpedienteProfissional':
        case 'ForaExpedienteRecurso':
            statusClasses = "is-bloqueado";
            tooltip = slot.motivo || "Horário bloqueado";
            icon = <Lock size={14} className="flex-shrink-0" />;
            break;
            
        default:
            statusClasses = "";
            tooltip += " - Indisponível";
            icon = <XCircle size={14} className="flex-shrink-0" />;
    }
    
    return (
        <div className={`${baseClasses} ${statusClasses}`} title={tooltip}>
            {icon}
            <span className="flex-grow font-medium">{slot.horario}</span>
        </div>
    );
};

const TurnoColuna = ({ titulo, horarios }) => (
    <div className="turno-coluna">
        <h4 className="turno-titulo">{titulo}</h4>
        <div className="time-slot-grid">
            {horarios.map((slot, idx) => (
                <TimeSlot key={`${titulo}-${slot.horarioISO || idx}`} slot={slot} />
            ))}
        </div>
    </div>
);

const DiaResultado = ({ resultadoDia, filtrosDeVisualizacao, resumoDaBusca, dataDaBusca }) => {
    const shareableRef = useRef(null);
    const { user: loggedInUser } = useMe();

    const handleShare = useCallback(() => {
        if (shareableRef.current === null) return;
        toPng(shareableRef.current, { cacheBust: true, pixelRatio: 2 })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = `agenda_${resumoDaBusca?.nome?.replace(/\s+/g, '_') || 'disponivel'}.png`;
                link.href = dataUrl;
                link.click();
            }).catch((err) => console.error('Erro ao gerar imagem:', err));
    }, [shareableRef, resumoDaBusca]);
    
    const horariosDoDia = useMemo(() => resultadoDia?.horarios || [], [resultadoDia]);

    const horariosFiltrados = useMemo(() => {
        if (!horariosDoDia || horariosDoDia.length === 0) return [];
        
        return horariosDoDia.filter(slot => {
            const status = slot.status;
            if (status === 'Intervalo') return filtrosDeVisualizacao.intervalos;
            if (status === 'Livre') return filtrosDeVisualizacao.livres;
            if (status === 'Ocupado') return filtrosDeVisualizacao.ocupados;
            if (status === 'Insuficiente') return filtrosDeVisualizacao.insuficientes;
            if (['Bloqueado', 'BloqueioManual', 'BloqueioAntecedencia', 'HorarioPassado', 'DataPassada', 'ForaExpedienteUnidade', 'ForaExpedienteProfissional', 'ForaExpedienteRecurso'].includes(status)) {
                return filtrosDeVisualizacao.bloqueados;
            }
            return false;
        });
    }, [horariosDoDia, filtrosDeVisualizacao]);

    const horariosPorTurno = useMemo(() => {
        const turnos = { manha: [], tarde: [], noite: [] };
        if (!horariosFiltrados || horariosFiltrados.length === 0) return turnos;
        
        horariosFiltrados.forEach(slot => {
            if (slot && typeof slot.horario === 'string') {
                const hora = parseInt(slot.horario.split(':')[0], 10);
                if (hora < 12) turnos.manha.push(slot);
                else if (hora < 18) turnos.tarde.push(slot);
                else turnos.noite.push(slot);
            }
        });
        return turnos;
    }, [horariosFiltrados]);

    const temHorariosParaMostrar = horariosPorTurno.manha.length > 0 || 
                                     horariosPorTurno.tarde.length > 0 || 
                                     horariosPorTurno.noite.length > 0;

    const temHorariosLivres = horariosFiltrados.some(h => h.status === 'Livre');

    // ✅ Estatísticas com diferenciação
    const estatisticas = useMemo(() => {
        const ocupadosProfissional = horariosDoDia.filter(h => h.status === 'Ocupado' && h.tipoOcupacao === 'Profissional').length;
        const ocupadosRecurso = horariosDoDia.filter(h => h.status === 'Ocupado' && (h.tipoOcupacao === 'Recurso' || !h.tipoOcupacao)).length;
        const livres = horariosDoDia.filter(h => h.status === 'Livre').length;
        const insuficientes = horariosDoDia.filter(h => h.status === 'Insuficiente').length;
        const bloqueados = horariosDoDia.filter(h => ['Bloqueado', 'BloqueioManual', 'BloqueioAntecedencia', 'HorarioPassado', 'DataPassada'].includes(h.status)).length;
        
        return { ocupadosProfissional, ocupadosRecurso, livres, insuficientes, bloqueados };
    }, [horariosDoDia]);

    const renderStatusDoDia = () => {
        if (horariosDoDia.length > 0 && !temHorariosParaMostrar) {
            return (
                <div className="panel-state-wrapper panel-info">
                    <Info size={24} className="icon" />
                    <span>Nenhum horário corresponde aos filtros selecionados</span>
                </div>
            );
        }

        switch (resultadoDia.statusDia) {
            case 'Folga': 
                return ( <div className="panel-state-wrapper panel-info"><CalendarOff size={24} className="icon"/><span>Dia de Folga</span></div> );
            case 'Folga ou Incompatível': 
                return ( <div className="panel-state-wrapper panel-warning"><GitPullRequestDraft size={24} className="icon"/><span>Dia de folga ou horários incompatíveis.</span></div> );
            case 'Horários Incompatíveis': 
                return ( <div className="panel-state-wrapper panel-error"><Combine size={24} className="icon"/><span>As agendas não possuem horários em comum.</span></div> );
            case 'Passado': 
                return ( <div className="panel-state-wrapper panel-muted"><TimerOff size={24} className="icon"/><span>Este dia já passou.</span></div> );
            default: 
                return ( <div className="panel-state-wrapper panel-info"><Info size={24} className="icon" /><span>Nenhum horário para exibir.</span></div> );
        }
    };
    
    const dataParaFormatar = useMemo(() => {
        const dataFonte = dataDaBusca || resultadoDia?.data;

        if (dataFonte && typeof dataFonte === 'string') {
            const parts = dataFonte.split('-');
            if (parts.length === 3) {
                const [year, month, day] = parts.map(Number);
                return new Date(year, month - 1, day);
            }
        }
        
        return new Date(); 
    }, [resultadoDia, dataDaBusca]);

    return (
        <div className="content-panel mb-6">
            <div className="panel-header flex justify-between items-center flex-wrap gap-2">
                <div>
                    <h3 className="panel-title capitalize">
                        {format(dataParaFormatar, "eeee, dd 'de' MMMM", { locale: ptBR })}
                    </h3>
                    {/* ✅ Exibe estatísticas resumidas com cores corretas */}
                    <div className="flex flex-wrap gap-3 mt-1 text-xs">
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span> 
                            Livres: {estatisticas.livres}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span> 
                            Ocup. Prof: {estatisticas.ocupadosProfissional}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span> 
                            Ocup. Recurso: {estatisticas.ocupadosRecurso}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span> 
                            Insuficientes: {estatisticas.insuficientes}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-gray-700"></span> 
                            Bloqueados: {estatisticas.bloqueados}
                        </span>
                    </div>
                </div>
                {temHorariosLivres && (
                    <button 
                        onClick={handleShare} 
                        className="action-button action-button-secondary py-1 px-2"
                    >
                        <Share2 size={16} className="icon"/> Compartilhar
                    </button>
                )}
            </div>

            {temHorariosParaMostrar ? (
                <div className="turnos-container">
                    {horariosPorTurno.manha.length > 0 && (<TurnoColuna titulo="Manhã" horarios={horariosPorTurno.manha} />)}
                    {horariosPorTurno.tarde.length > 0 && (<TurnoColuna titulo="Tarde" horarios={horariosPorTurno.tarde} />)}
                    {horariosPorTurno.noite.length > 0 && (<TurnoColuna titulo="Noite" horarios={horariosPorTurno.noite} />)}
                </div>
            ) : (
                renderStatusDoDia()
            )}

            <div style={{ position: 'fixed', top: '-3000px', left: 0, zIndex: -1 }}>
                <AgendaShareable
                    ref={shareableRef}
                    itemSelecionado={resumoDaBusca}
                    data={dataDaBusca}
                    horarios={horariosDoDia}
                    accountName={loggedInUser?.account?.name || 'Seu Negócio'}
                />
            </div>
        </div>
    );
};

const ResultadoDisponibilidade = ({ dias, isSingleDay, filtrosDeVisualizacao, resumoDaBusca, dataDaBusca }) => {
    if (!dias || dias.length === 0) {
        return (
            <div className="content-panel mt-6">
                <div className="panel-state-wrapper panel-info">
                    <Info size={32} className="icon" />
                    <span className="text-lg">Use os filtros acima para iniciar uma busca.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {dias.map((dia, idx) => (
                <DiaResultado 
                    key={dataDaBusca || dia.entidade || idx} 
                    resultadoDia={dia} 
                    filtrosDeVisualizacao={filtrosDeVisualizacao} 
                    resumoDaBusca={resumoDaBusca}
                    dataDaBusca={dataDaBusca}
                />
            ))}
        </div>
    );
};

export default ResultadoDisponibilidade;